import { useState } from 'react';

import { debounce } from '../debounce';

import type { RefObject, UIEvent, MouseEvent as ReactMouseEvent } from 'react';

type Options = {
  /**
   * Whether the draggable scrolling functionality is enabled.
   */
  enabled?: boolean;

  /**
   * Specify the direction draggable directions.
   * Defaults to `both`.
   */
  direction?: 'vertical' | 'horizontal' | 'both';

  /**
   * A function which gets invoked once when dragging started.
   */
  onDragStart?: (event: ReactMouseEvent<HTMLElement>) => void;

  /**
   * A functions which gets invoked while dragging.
   */
  onDrag?: (x: number, y: number) => void;

  /**
   * A functions which gets invoked once when dragging ended.
   */
  onDragEnd?: () => void;
};

// Slightly improved & modified version of MIT licensed
// `g-delmo/use-draggable-scroll`
// https://github.com/g-delmo/use-draggable-scroll/blob/master/src/index.tsx.

/**
 * Returns a set of events you can assign to a component to enable scrolling through dragging.
 *
 * ```tsx
 * const ref = useRef<HTMLDivElement>();
 * const { onMouseDown, onScroll } = useDraggableScroll(ref);
 *
 * return (
 *   <div onMouseDown={onMouseDown} onScroll={onScroll}>
 *     ...
 *   </div>
 * );
 * ```
 */
export const useDraggableScroll = (
  ref: RefObject<HTMLElement | undefined | null>,
  options: Options = { direction: 'both' },
) => {
  const { direction, enabled = true } = options;
  const [dragging, setDragging] = useState(false);

  // The initial position (scroll progress and mouse location) when the mouse is pressed down on the element
  let initialPosition = { scrollTop: 0, scrollLeft: 0, mouseX: 0, mouseY: 0 };

  const mouseMoveHandler = (event: MouseEvent) => {
    if (!ref.current) {
      return;
    }

    // Calculate differences to see how far the user has moved
    const dx = event.clientX - initialPosition.mouseX;
    const dy = event.clientY - initialPosition.mouseY;

    // Scroll the element according to those differences
    if (direction !== 'horizontal') {
      ref.current.scrollTop = initialPosition.scrollTop - dy;
    }

    if (direction !== 'vertical') {
      ref.current.scrollLeft = initialPosition.scrollLeft - dx;
    }

    if (options.onDrag) {
      // Pass only the numeric differences
      options.onDrag(dx, dy);
    }
  };

  const mouseUpHandler = (event: MouseEvent) => {
    // Return to cursor: grab after the user is no longer pressing
    if (ref.current) {
      ref.current.style.cursor = '';
      ref.current.style.scrollBehavior = '';
    }

    event.preventDefault();

    setDragging(false);

    // Remove the event listeners since it is not necessary to track the mouse position anymore
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);

    if (options.onDragEnd) {
      options.onDragEnd();
    }
  };

  const onMouseDown = (event: ReactMouseEvent<HTMLElement>) => {
    if (!ref.current || !enabled) {
      return;
    }

    // Disables dragging on button and anchor elements
    if (
      event.target instanceof HTMLElement &&
      event.target.matches('button, button *, a, a *')
    ) {
      return;
    }

    event.preventDefault();

    setDragging(true);

    // Save the position at the moment the user presses down
    initialPosition = {
      scrollLeft: ref.current.scrollLeft,
      scrollTop: ref.current.scrollTop,
      mouseX: event.clientX,
      mouseY: event.clientY,
    };

    // Show a cursor: grabbing style and set user-select: none to avoid highlighting text while dragging
    ref.current.style.cursor = 'grabbing';
    ref.current.style.userSelect = 'none';

    ref.current.style.scrollBehavior = 'auto';
    ref.current.style.scrollSnapType = 'none';

    // Add the event listeners that will track the mouse position for the rest of the interaction
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);

    if (options.onDragStart) {
      // Pass the event to onDragStart
      options.onDragStart(event);
    }
  };

  const handleSnapTypeReset = debounce(() => {
    if (!ref.current || dragging) {
      return;
    }

    ref.current.style.scrollSnapType = '';
  }, 50);

  const onScroll = (event: UIEvent<HTMLElement>) => {
    if (!enabled) {
      return;
    }

    if (dragging) {
      return;
    }

    handleSnapTypeReset();
  };

  return {
    onMouseDown,
    onScroll,
  };
};
